import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { PdfBuilderDataShaperService } from './pdf-builder-data-shaper.service';
import { PdfBuilderRenderBlocksService } from './pdf-builder-render-blocks.service';
import { PdfRenderContext } from './pdf-render-context';
import { PdfBuilderUtilitiesService } from './pdf-builder-utilities.service';

@Injectable({
  providedIn: 'root'
})
export class PdfBuilderService {
  private bottomMargin: number = 270;
  private leftMargin: number = 10;
  private rightMargin: number = 10;
  private headingFont: [string, string] = ['helvetica', 'bold'];
  private subheadingFont: [string, string] = ['helvetica', 'bold'];
  private inputTextFont: [string, string] = ['helvetica', 'normal'];
  private inputFontSize = 11;
  private titleFontSize = 14;
  private fontColor: [number, number, number] = [70,70,70];
  private context: PdfRenderContext;

  constructor(
    private dataShaper: PdfBuilderDataShaperService,
    private renderBlocks: PdfBuilderRenderBlocksService,
    private pdfUtils: PdfBuilderUtilitiesService
  ) {
  }

  private initializeContext(logo: HTMLImageElement): void {
    this.context = {
      doc: new jsPDF(),
      currentY: 30,
      leftMargin: this.leftMargin,
      rightMargin: this.rightMargin,
      bottomMargin: this.bottomMargin,
      inputTextFont: this.inputTextFont,
      headingFont: this.headingFont,
      subheadingFont: this.subheadingFont,
      inputFontSize: this.inputFontSize,
      titleFontSize: this.titleFontSize,
      fontColor: this.fontColor,
      logo: logo,
      // footerData
    };
  
    this.renderBlocks.initialize(this.context);
  }

  private destroyContext(): jsPDF {
    const output = this.context.doc;
    this.context = null;
    return output
  }

  public async generateMedicalAttentionPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapeAttentionData(raw);
    
    this.renderBlocks.setFooter(shaped.footer);
    this.renderBlocks.addHeader("HISTORIA CLÍNICA");

    this.renderAffiliationSection('I. Afiliación', shaped.affiliation, shaped.metadata, shaped.anamnesis.reason);
    this.renderAnamnesisSection('II. Anamnesis', shaped.anamnesis, shaped.metadata );
    this.renderClinicalExamSection('III. Examen Clínico', shaped.clinicalExam );
    this.renderDiagnosisSection('IV. Diagnósticos', shaped.diagnosis);
    this.renderTreatmentSection('V. Tratamientos', shaped.treatment);

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  public async generateTriagePdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapeTriageData(raw);
    this.renderBlocks.setFooter(shaped.footer);

    this.renderBlocks.addHeader("TRIAJE");

    this.renderBasicPatientInfoSection('I. Datos', shaped.patientData);
    this.renderAttentionMetadataSection('II. Consulta', shaped.appointmentReason, shaped.metadata);
    this.renderBiologicalFunctionsSection('III. Funciones Biológicas', shaped.biologicalFunctions);
    this.renderVitalSignsSection('IV. Funciones Vitales', shaped.vitalSigns);

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  public async generatePrescriptionPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapePrescriptionData(raw);
    this.renderBlocks.setFooter(shaped.footer);

    this.renderBlocks.addHeader("RECETA MÉDICA");

    this.renderBasicPatientInfoSection('I. Datos', shaped.patientData, shaped.metadata);
    this.renderDiagnosisSection('II. Diagnósticos', shaped.diagnosis);
    this.renderTreatmentSection('III. Medicamentos', shaped.medications, { spacing: 160 });

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  public async generateExamsPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);

    const shaped = this.dataShaper.shapeExamData(raw);
    this.renderBlocks.setFooter(shaped.footer);

    this.renderBlocks.addHeader("EXÁMENES E IMÁGENES MÉDICAS");

    this.renderBasicPatientInfoSection('I. Datos', shaped.patientData, shaped.metadata);
    this.renderDiagnosisSection('II. Diagnósticos', shaped.diagnosis);
    this.renderExamSection('III. Exámenes e Imágenes', shaped.exams, { spacing: 160 });

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  public async generateInterconsultationPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapeInterconsultationData(raw);
    this.renderBlocks.setFooter(shaped.footer);
    this.renderBlocks.addHeader("INTERCONSULTA");

    this.renderBasicPatientInfoSection('I. Datos del Paciente', shaped.patientData);
    this.renderInterconsultationSection('II. Detalles de la Solicitud', shaped.interconsultation, shaped.metadata);
    this.renderDiagnosisSection('III. Diagnósticos', shaped.diagnosis);

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  public async generateAffiliationPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapeAffiliationData(raw);
    this.renderBlocks.addHeader("HISTORIA CLÍNICA");

    this.renderDetailedPatientInfoSection('I. Datos del Paciente', shaped.patientData)

    const output = this.destroyContext();

    return output;
  }

  public async generateNotesPdf(raw: any, logo: HTMLImageElement): Promise<jsPDF> {
    this.initializeContext(logo);
    const shaped = this.dataShaper.shapeNotesData(raw);
    this.renderBlocks.setFooter(shaped.footer);

    const isNursingNotes = shaped.notes.specialty === 'enfermeria';
    const title = isNursingNotes ? "NOTAS DE ENFERMERÍA" : "NOTAS";

    this.renderBlocks.addHeader(title);

    if (!isNursingNotes) {
      this.renderSpecialtySection("Especialidad", shaped.notes.specialty);
    }

    this.renderNoteDetailsSection("Detalles", shaped.notes.notes);

    await this.renderBlocks.addSignatureSection(shaped.signature);
    this.renderBlocks.addFooter();

    const output = this.destroyContext();

    return output;
  }

  private renderInterconsultationSection(title: string, interconsultationData: any, metadata: any) {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    this.renderBlocks.renderTextBlock("Fecha: ", metadata.dateReport, "bold", {inlineLabel: true});
    // const fields: { label: string; value: string }[] = [
    //   { label: 'Especialidad Referente', value: interconsultationData.specialtyFrom },
    //   { label: 'Especialidad Consultada', value: interconsultationData.specialtyTo },
    // ];
    // this.renderBlocks.renderLabelValueGrid(fields);

    this.renderBlocks.renderTextBlock('Especialidad Referente:', interconsultationData.specialtyFrom)

    const referrals = interconsultationData.referrals;

    if (referrals.length === 0) {
      this.renderBlocks.renderTextBlock('', 'Ninguno', 'normal', {contentSpacing: 0});
      return;
    }

    // const isLegacy = referrals.every(r => !r.code); // if no codes exist

    // if (isLegacy) {
    //   // Just render all specialties in a comma-separated line
    //   console.log(interconsultationData.referrals);
    //   const flatSpecialties = referrals.map(r => r.specialty).join(', ');
    //   this.renderBlocks.renderTextBlock('Especialidades Consultadas:', flatSpecialties);
    // } else {
      // Use grouped header layout
      this.renderBlocks.renderHeading('Especialidad Consultadas:', {bottomPadding: 6});
      const interconsultationGroups = referrals.map(r => ({
        header: '- ' + r.specialty,
        values: [
          { label: '', value: r.name ?? ''},
          { label: 'CPT', value: r.code ?? ''},
        ],
        footer: r.reason ? `Motivo: ${r.reason}` : undefined
      }));

      this.renderBlocks.renderGroupedLabelValueRowsWithHeader(interconsultationGroups, {
        columnWidths: [160, 80],
        paddingLeft: 0
      });
    // }


    // this.renderBlocks.renderTextBlock("Motivo: ", interconsultationData.reason);
    this.renderBlocks.renderTextBlock("Exámen Físico: ", interconsultationData.physicalExamSummary);

    this.renderBlocks.addVerticalSpacing();
  }

  private renderSpecialtySection(title: string, specialty: string) {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    this.renderBlocks.renderTextBlock("", specialty);
    this.renderBlocks.addVerticalSpacing();
  }

  private renderNoteDetailsSection(title: string, details: string) {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    this.renderBlocks.renderTextBlock("", details);
    this.renderBlocks.addVerticalSpacing();
  }

  private renderBasicPatientInfoSection(title: string, patientData: any, metadata?: any) {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    const fields: { label: string; value: string }[] = [
      { label: 'Nombre Completo', value: patientData.fullName },
      { label: 'Edad', value: patientData.age },
      { label: 'Sexo', value: patientData.sex },
      { label: 'DNI', value: patientData.dni },
    ];
    if (metadata && metadata.dateReport) {
      fields.push({ label: 'Fecha', value: metadata.dateReport });
    }

    this.renderBlocks.renderLabelValueGrid(fields);

    this.renderBlocks.addVerticalSpacing();
  }

  private renderDetailedPatientInfoSection(title: string, patientData: any) {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    const fields: { label: string; value: string }[] = [
      { label: 'Nombre Completo', value: patientData.fullName },
      { label: 'Fecha de Nacimiento', value: patientData.birthDate },
      { label: 'Sexo', value: patientData.sex },
      { label: 'DNI', value: patientData.dni },
      { label: 'Ocupación', value: patientData.occupation },
      { label: 'Grado de Instrucción', value: patientData.education },
      { label: 'Estado Civil', value: patientData.maritalStatus },
      { label: 'Email', value: patientData.email },
      { label: 'País', value: patientData.country },
      { label: 'Dirección', value: patientData.address },
      { label: 'Teléfono', value: patientData.phone },
      { label: 'Aseguradora', value: patientData.insurance },
      { label: 'Nro de Póliza', value: patientData.insuranceNumber },
      { label: 'Fecha de Afiliación', value: patientData.affiliationDate },
    ];
  
    this.renderBlocks.renderLabelValueGrid(fields);

    this.renderBlocks.addVerticalSpacing();
  }

  private renderAttentionMetadataSection(title: string, appointmentReason: any, metadata: any ): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);

    this.renderBlocks.renderTextBlock("Fecha: ", metadata.dateReport, "bold", {inlineLabel: true});

    this.renderBlocks.renderTextBlock("Motivo de consulta: ", appointmentReason.reason);
    // Extra notes for hospitalized evaluations
    const isHospitalization = metadata.isHospitalization;
    if (isHospitalization) {
      this.renderBlocks.renderTextBlock("Anotaciones Adicionales:", appointmentReason.additionalComments);
    }

    this.renderBlocks.addVerticalSpacing();
  }

  private renderAffiliationSection(title: string, affiliation: any, metadata: any, reason: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    
    const fields: { label: string; value: string }[] = [
      { label: 'Nombre Completo', value: affiliation.fullName },
      { label: 'Edad', value: affiliation.age },
      { label: 'Sexo', value: affiliation.sex },
      { label: 'DNI', value: affiliation.dni },
      { label: 'Grupo de sangre', value: affiliation.bloodType },
      { label: 'Fecha y Hora', value: metadata.dateReport },
      // { label: 'NHC', value: affiliation.nhc },
      { label: 'Seguro', value: affiliation.insurance },
      { label: 'Tipo de consulta', value: metadata.consultType },
      { label: 'N° Seguro', value: affiliation.insuranceNumber },
    ];
  
    this.renderBlocks.renderLabelValueGrid(fields);

    this.renderBlocks.renderTextBlock("Motivo de consulta: ", reason);
    
    this.renderBlocks.addVerticalSpacing();
  }

  private renderAnamnesisSection(title: string, anamnesis: any, metadata: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
  
    // Use metadata for conditional logic
    const isEvaluation = metadata.category === 'evaluation';
    const isHospitalization = metadata.isHospitalization;
  
    // Timing and main symptoms
    this.renderSicknessTiming(anamnesis);  // You may need to pass additional fields if needed
    // this.renderSymptomsList(anamnesis);    // Same here
    this.renderBlocks.renderTextBlock("Síntomas principales:", anamnesis.symptomsList)
    
  
    // Current illness narrative
    this.renderBlocks.renderTextBlock("Relato:", anamnesis.currentIllness)

  
    // Biological functions: only if not an evaluation
    if (!isEvaluation) {
      this.renderBiologicalFunctions(anamnesis.biologicalFunctions);
    }
  
    // Extra notes for hospitalized evaluations
    if (isHospitalization && isEvaluation) {
      this.renderBlocks.renderTextBlock("Anotaciones Adicionales:", anamnesis.additionalComments);
    }
  
    // Antecedents: only for attention-type consults
    if (metadata.category === 'attention') {
      this.renderAntecedentes(anamnesis);
    }

    this.renderBlocks.addVerticalSpacing();
  }

  private renderBiologicalFunctionsSection(title: string, biologicalFunctions: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    this.renderBiologicalFunctions(biologicalFunctions, false);
    this.renderBlocks.addVerticalSpacing();
  }

  private renderVitalSignsSection(title: string, vitalSigns: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    // this.renderBlocks.renderLabelValueGrid(vitalSigns, { numColumns: 3, columnWidth: 64 });
    this.renderBlocks.renderLabelValueGrid(vitalSigns, { 
      numColumns: 3, 
      columnWidth: 64,
      drawBorders: true,
      drawInnerDivider: true,
      paddingLeft: 2,
      borderColumnSpacing: 2,
      rowSpacing: 1.5
    });
    this.renderBlocks.addVerticalSpacing();
  }

  private renderClinicalExamSection(title: string, clinicalExam: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    // this.renderBlocks.renderLabelValueGrid(clinicalExam.vitalSigns, { numColumns: 3, columnWidth: 64 });
    this.renderBlocks.renderLabelValueGrid(clinicalExam.vitalSigns, { 
      numColumns: 3, 
      columnWidth: 64,
      drawBorders: true,
      drawInnerDivider: true,
      paddingLeft: 2,
      borderColumnSpacing: 2,
      rowSpacing: 1.5
    });
    this.renderBlocks.addVerticalSpacing(2);
    this.renderBlocks.renderTextBlock('Estado General: ', clinicalExam.general.generalStatus);
    this.renderBlocks.renderTextBlock('Descripción General: ', clinicalExam.general.description);
    this.renderBlocks.renderHeading('Detalles:', {bottomPadding: 6})
    for (const section of clinicalExam.physicalExam) {
      this.renderBlocks.renderTextBlock(section.label, section.value, "underline", {inlineLabel: true});
    }
    this.renderBlocks.addVerticalSpacing();
  }

  private renderDiagnosisSection(
    title: string, 
    diagnosis: {
      diagnosisList: { value: string; code: string }[],
      diagnosisObservations?: string | null,
    },
  ): void {
    const diagnosisList = diagnosis.diagnosisList ?? [];
    const diagnosisObservations = diagnosis.diagnosisObservations ?? 'No especificadas';

    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);

    // const flattened = diagnosisList?.flatMap(d => [
    //   { label: '', value: d.value },
    //   { label: 'CIE-10', value: d.code }
    // ]);
    const flattened = diagnosisList.reduce((acc, d) => {
      acc.push({ label: '', value: d.value });
      acc.push({ label: 'CIE-10', value: d.code });
      return acc;
    }, [] as { label: string; value: string }[]);
    if (diagnosisList.length === 0) {
      this.renderBlocks.renderTextBlock('', 'Ninguno', 'normal', {contentSpacing: 4.5});
      this.renderBlocks.addVerticalSpacing();
      return;
    }
    this.renderBlocks.renderLabelValueGrid(flattened, { numColumns: 2, columnWidth: 160 });
    this.renderBlocks.renderTextBlock('Observaciones: ', diagnosisObservations, 'bold', { inlineLabel: true });
    this.renderBlocks.addVerticalSpacing();
  }

  private renderTreatmentSection(title: string, 
    treatmentList: { label: string; value: string }[],
    options?: {
      spacing?: number;
    }
  ): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    if (treatmentList.length === 0) {
      this.renderBlocks.renderTextBlock('', 'Ninguno', 'normal', {contentSpacing: 4.5});
      this.renderBlocks.addVerticalSpacing();
      return;
    }

    const spacing = options?.spacing ? options?.spacing : 142;

    this.renderBlocks.renderLabelValueGrid(treatmentList, { numColumns: 2, columnWidth: spacing });
    this.renderBlocks.addVerticalSpacing();
  }

  private renderExamSection(title: string, 
    exams: {
      examList: { label: string; value: string }[],
      examObservations?: string | null,
    },
    options?: {
      spacing?: number;
    }
  ): void {
    const examList = exams.examList;
    const examObservations = exams.examObservations ?? '';

    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderSectionTitle(title);
    if (examList.length === 0) {
      this.renderBlocks.renderTextBlock('', 'Ninguno', 'normal', {contentSpacing: 4.5});
      this.renderBlocks.addVerticalSpacing();
      return;
    }

    const spacing = options?.spacing ? options?.spacing : 150;

    this.renderBlocks.renderLabelValueGrid(examList, { numColumns: 2, columnWidth: spacing });
    this.renderBlocks.renderTextBlock('Observaciones: ', examObservations, 'bold', { inlineLabel: true });
    this.renderBlocks.addVerticalSpacing();
  }
  
  private renderSicknessTiming(anamnesis: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    const text = `${anamnesis.sicknessTime ?? '?'} ${anamnesis.sicknessUnit ?? ''}`.trim();
    this.renderBlocks.renderHeading('Tiempo de Enfermedad: ')
    this.renderBlocks.renderText(text, { leftPadding: 50, bottomPadding: 8 });
  }
  
  private renderBiologicalFunctions(bio: { label: string; value: string }[], showTitle = true): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    if (showTitle) {
      this.renderBlocks.renderHeading('Funciones biológicas:', {bottomPadding: 7})
    }

    this.renderBlocks.renderLabelValueGrid(bio, {
      drawBorders: true, 
      drawInnerDivider: true,
      paddingLeft: 2, 
      borderColumnSpacing: 4,
      rowSpacing: 1.5
    });
    this.renderBlocks.addVerticalSpacing(2);
  }
  
  private renderAntecedentes(anamnesis: any): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    this.renderBlocks.renderHeading('Antecedentes:', {bottomPadding: 8})
    this.renderBlocks.renderSubheading('Personales:');
    this.renderPersonalHistoryCheckboxes(anamnesis.personalHistory);
    this.renderBlocks.renderTextBlock('Otros (especificar):', anamnesis.personalHistory, 'normal');
    if (anamnesis.specialty?.toUpperCase() !== 'PSICOLOGIA') {
      this.renderBlocks.renderTextBlock('Del Padre:', anamnesis.paternalHistory, 'underline');
      this.renderBlocks.renderTextBlock('De la Madre:', anamnesis.maternalHistory, 'underline');
    }
    this.renderBlocks.renderTextBlock('Alergias:', anamnesis.allergies);
    this.renderBlocks.renderTextBlock('Medicamentos:', anamnesis.medications);
  }

  private renderPersonalHistoryCheckboxes(backgroundText: string): void {
    this.renderBlocks.addNewPageIfNeeded(20);
    const conditions = [
      { label: 'HTA', keywords: ['Hipertension', 'Arterial'] },
      { label: 'Diabetes', keywords: ['Diabetes'] },
      { label: 'RAMS', keywords: ['RAMS'] },
      { label: 'TBC', keywords: ['Tuberculosis'] },
      { label: 'Alcohol', keywords: ['Alcohol'] },
      { label: 'Tabaco', keywords: ['Tabaco'] },
      { label: 'Asma', keywords: ['Asma'] },
      { label: 'Hepatitis', keywords: ['Hepatitis'] },
      { label: 'Convulsiones', keywords: ['Convulsiones'] },
      { label: 'Fracturas', keywords: ['Fracturas'] },
    ];

    const entries = conditions.map(cond => ({
      label: cond.label,
      checked: cond.keywords.every(kw => backgroundText.includes(kw)),
    }));
  
    this.renderBlocks.renderCheckboxGrid(entries, {
      columns: 5,
      rowHeight: 7,
      colWidth: 40,
      checkboxSize: 3,
      paddingBelow: 2,
    });
  }
}

